<template>
    <div class="work-order-table">
        <div class="table-container">
            <table>
                <thead>
                <tr>
                    <th v-if="showCheckbox" style="width: 50px;">
                        <input type="checkbox" v-model="selectAll" @change="handleSelectAll">
                    </th>
                    <th v-for="column in columns" :key="column.key" :style="getColumnStyle(column)">
                        {{ column.title }}
                    </th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, index) in paginatedList" :key="item.id || item.key"
                    :class="{'highlight': highlightedRowId === (item.id || item.key) }"
                    @click="handleRowClick(item, $event)">
                    <td v-if="showCheckbox">
                        <input type="checkbox"
                               v-model="selectedItems[item.id || item.key]"
                               @change.stop="handleSelectionChange">
                    </td>
                    <td v-for="column in columns" :key="column.key" :style="getColumnStyle(column)">
                        <!-- 表格内容渲染逻辑保持不变 -->
                        <template v-if="column.infotype === 'index'">
                            {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                        </template>
                        <template v-else-if="column.type === 'priority'">
                            <span class="priority-badge" :class="'priority-' + (item[column.field] || '').toLowerCase()"></span>
                            {{ item[column.field] || '-' }}
                        </template>
                        <template v-else-if="column.infotype === 'status'">
                            <span class="status-badge"
                                  :style="{ color: getStatusStyle(item[column.field]).color, backgroundColor: getStatusStyle(item[column.field]).background }">
                              {{ getStatusText(item[column.field]) }}
                            </span>
                        </template>
                        <template v-else-if="column.infotype === 'template' && !column.render && column.key !== 'actions'">
                            <slot :name="`template-${column.key}`" :item="item">
                                {{ item[column.field] || '-' }}
                            </slot>
                        </template>
                        <template v-else-if="column.infotype === 'template' && column.render && typeof column.render === 'function'">
                            <div v-html="column.render(item)" @click.stop="handleAction(item, $event, 'template-click', null, index)"></div>
                        </template>
                        <template v-else-if="column.render && typeof column.render === 'function'">
                            <div v-html="column.render(item)" @click.stop="handleAction(item, $event, 'template-click', null, index)"></div>
                        </template>
                        <template v-else-if="column.key === 'actions' && column.infotype != 'template'">
                            <div class="action-btns" style="position: sticky; right: 0;">
                                <template v-if="column.render.custom">
                                    <button
                                        v-for="action in column.customList"
                                        :key="action"
                                        class="action-btn custom-btn"
                                        @click.stop="handleAction(item, $event, 'custom', action, index)">
                                        {{ action }}
                                    </button>
                                </template>
                            </div>
                        </template>
                        <template v-else-if="column.key === 'actions' && column.infotype === 'template'">
                            <slot name="actions" :item="item" :index="index"></slot>
                        </template>
                        <template v-else>
                            <span v-if="column.ellipsis" class="ellipsis-with-tooltip" :title="item[column.field] || '-'"
                                  :style="column.style || ''">
                              {{ item[column.field] || '-' }}
                            </span>
                            <span v-else>{{ item[column.field] || '-' }}</span>
                        </template>
                    </td>
                </tr>
                <!-- 当没有数据时显示暂无数据 -->
                <tr v-if="paginatedList.length === 0">
                    <td :colspan="showCheckbox ? columns.length + 1 : columns.length" class="no-data">
                        暂无数据
                    </td>
                </tr>
                </tbody>
            </table>
        </div>
        <!-- 分页 -->
        <div class="pagination" v-if="pageShow">
            <button class="page-btn" :disabled="currentPage === 1" @click="prevPage">
                &lt;
            </button>
            <button class="page-btn" :class="{ active: currentPage === page }"
                    v-for="page in visiblePages" :key="page" @click="gotoPage(page)">
                {{ page }}
            </button>
            <button class="page-btn" :disabled="currentPage === totalPages" @click="nextPage">
                &gt;
            </button>
            <div class="page-info">
                第 {{ currentPage }} 页 / 共 {{ totalPages }} 页
            </div>
        </div>
        <!-- 提交按钮 -->
        <div class="submit-btn" v-if="showCheckbox">
            <ButtonField text="保存" primary @click="submitSelection"/>
        </div>
    </div>
</template>
<script>
export default {
    name: 'WorkOrderTable',
    props: {
        list: {
            type: Array,
            default: () => []
        },
        columns: {
            type: Array,
            required: true
        },
        tableTitle: {
            type: String,
            default: '工单列表'
        },
        itemsPerPage: {
            type: Number,
            default: 10
        },
        showCheckbox: {
            type: Boolean,
            default: true
        },
        pageShow: {
            type: Boolean,
            default: true
        },
        totalSize: {
            type: Number,
            default: 0
        },
        totalPages: {
            type: Number,
            default: 1
        },
        pageNum: {
            type: Number,
            default: 1
        },
        selectedItems: {
            type: Object,
            default: () => {}
        }
    },
    data() {
        return {
            currentPage: 1,
            selectAll: false,
            highlightedRowId: null, // 存储当前高亮行的ID
            isRowClicked: false // 标记是否有行被点击
        }
    },
    computed: {
        paginatedList() {
            return this.list;
        },
        visiblePages() {
            if (this.totalPages <= 1) {
                return [1];
            }

            const range = 2;
            const pages = [];

            pages.push(1);

            const start = Math.max(2, this.currentPage - range);
            const end = Math.min(this.totalPages - 1, this.currentPage + range);

            if (start > 2) {
                pages.push('...');
            }
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            if (end < this.totalPages - 1) {
                pages.push('...');
            }
            pages.push(this.totalPages);

            return pages;
        }
    },
    watch: {
        pageNum(newVal) {
            this.currentPage = newVal;
        },
        totalSize(newVal) {
            this.totalPages = Math.ceil(newVal / this.itemsPerPage);
        },
        selectedItems(newVal) {
            this.selectedItems = newVal;
        }
    },
    methods: {
        handleRowClick(item, event) {
            event.stopPropagation();
            // 获取当前行ID
            const rowId = item.id || item.key;

            // 如果点击的是已高亮的行，则取消高亮
            if (this.highlightedRowId === rowId) {
                this.highlightedRowId = null;
                this.isRowClicked = false;
            } else {
                // 否则高亮当前行
                this.highlightedRowId = rowId;
                this.isRowClicked = true;
            }

            this.$emit('row-click', item);
        },
        handleSelectAll() {
            if (this.selectAll) {
                this.list.forEach(item => {
                    this.selectedItems[item.id || item.key] = true;
                });
            } else {
                this.list.forEach(item => {
                    delete this.selectedItems[item.id || item.key];
                });
            }
            this.$emit('selection-change', this.selectedItems);
        },
        handleSelectionChange() {
            const allSelected = this.list.every(item => this.selectedItems[item.id || item.key]);
            this.selectAll = allSelected;
            this.$emit('selection-change', this.selectedItems);
        },
        submitSelection() {
            this.$emit('submit-selection', this.selectedItems);
        },
        openSettings() {
            this.$emit('settings');
        },
        getStatusStyle(statusValue) {
            const statusContent = this.columns.find(col => col.key === 'logType')?.statusContent || [];
            const status = statusContent.find(s => s.value === statusValue);
            return status || { color: '#94a3b8', background: 'rgba(0, 0, 0, 0.1)' };
        },
        getStatusText(statusValue) {
            const statusContent = this.columns.find(col => col.key === 'logType')?.statusContent || [];
            const status = statusContent.find(s => s.value === statusValue);
            return status?.text || '-';
        },
        highlightRow(id) {
            this.highlightedRowId = id;
        },
        unhighlightRow() {
            this.highlightedRowId = null;
        },
        isRowHighlighted(id) {
            return this.highlightedRowId === id;
        },
        prevPage() {
            if (this.currentPage > 1) {
                this.currentPage--;
                this.$emit('page-change', this.currentPage);
            }
        },
        nextPage() {
            if (this.currentPage < this.totalPages) {
                this.currentPage++;
                this.$emit('page-change', this.currentPage);
            }
        },
        gotoPage(page) {
            if (page === '...') return;
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page;
                this.$emit('page-change', this.currentPage);
            }
        },
        handleAction(item, event, actionType, action = null, index = null) {
            const actionData = {
                type: actionType,
                item: item,
                id: item.id || item.key,
                index: index
            };

            if (actionType === 'custom' && action) {
                actionData.action = action;
            }

            this.$emit('action', actionData);
        },
        getColumnStyle(column) {
            const style = {};
            if (column.width) {
                style.width = column.width.includes('%') || column.width.includes('px')
                    ? column.width
                    : `${column.width}px`;
                style.minWidth = style.width;
            }
            if (column.fixed) {
                style.position = 'sticky';
                if (column.fixed === 'left') {
                    style.left = 0;
                } else if (column.fixed === 'right') {
                    style.right = 0;
                }
                style.zIndex = 2;
            }
            return style;
        }
    }
}
</script>
<style scoped>
.work-order-table {
    width: 100%;
}

.table-container {
    overflow-x: auto;
}
.ellipsis-with-tooltip {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: help;
}

.table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
}

.table-title {
    font-size: 1.1rem;
    color: #00ff96;
}

.table-actions {
    display: flex;
    gap: 0.75rem;
}

.icon-btn {
    width: 36px;
    height: 36px;
    background: rgba(0, 255, 150, 0.1);
    border: 1px solid rgba(0, 255, 150, 0.3);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    color: #94a3b8;
}

.icon-btn:hover {
    background: rgba(0, 255, 150, 0.2);
    border-color: #00ff96;
    color: #00ff96;
}

table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

th {
    background: rgba(10, 20, 40, 0.6);
    padding:0.5rem 1rem;
    text-align: left;
    font-weight: 600;
    color: #00ff96;
    font-size: 0.9rem;
    border-bottom: 2px solid rgba(0, 255, 150, 0.3);
}

th:first-child {
    border-radius: 8px 0 0 0;
}

th:last-child {
    border-radius: 0 8px 0 0;
}

td {
    padding: 0.5rem;
    border-bottom: 1px solid rgba(0, 255, 150, 0.1);
    font-size: 0.9rem;
    color:rgba(255,255,255,0.8);

    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

/* 移除鼠标悬停高亮效果 */
tr:not(.highlight):hover td {
    background: rgba(0, 255, 150, 0.05);
}

/* 点击选中高亮样式 */
tr.highlight td {
    background: rgba(0, 255, 150, 0.2);
    border-top: 1px solid rgba(0, 255, 150, 0.4);
    border-bottom: 1px solid rgba(0, 255, 150, 0.4);
    transition: background 0.2s ease;
}

tbody {
    display: block;
    height: calc(35vh  - 1rem - 136px);
    overflow-y: auto;
}

tr {
    display: table;
    width: 100%;
    table-layout: fixed;
}
/* 状态标签 */
.status-badge {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 500;
}

.status-pending {
    background: rgba(255, 193, 7, 0.2);
    color: #ffc107;
    border: 1px solid rgba(255, 193, 7, 0.3);
}

.status-processing {
    background: rgba(0, 123, 255, 0.2);
    color: #007bff;
    border: 1px solid rgba(0, 123, 255, 0.3);
}

.status-completed {
    background: rgba(0, 255, 150, 0.2);
    color: #00ff96;
    border: 1px solid rgba(0, 255, 150, 0.3);
}

.status-overdue {
    background: rgba(255, 107, 107, 0.2);
    color: #ff6b6b;
    border: 1px solid rgba(255, 107, 107, 0.3);
}

/* 优先级标签 */
.priority-badge {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 0.5rem;
}

.priority-high {
    background: #ff6b6b;
    box-shadow: 0 0 8px rgba(255, 107, 107, 0.5);
}

.priority-medium {
    background: #ffc107;
    box-shadow: 0 0 8px rgba(255, 193, 7, 0.5);
}

.priority-low {
    background: #00ff96;
    box-shadow: 0 0 8px rgba(0, 255, 150, 0.5);
}

/* 操作按钮 */
.action-btns {
    display: flex;
    gap: 0.5rem;
}

.action-btn {
    width: 85px!important;
    padding: 0.25rem 0.5rem;
    background: rgba(0, 255, 150, 0.1);
    border: 1px solid rgba(0, 255, 150, 0.3);
    border-radius: 4px;
    color: #94a3b8;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.3s ease;
}

.action-btn:hover {
    background: rgba(0, 255, 150, 0.2);
    color: #00ff96;
    border-color: #00ff96;
}

/* 分页 */
.pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.1rem;
}

.page-btn {
    width: 36px;
    height: 36px;
    background: rgba(20, 35, 70, 0.5);
    border: 1px solid rgba(0, 255, 150, 0.2);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    color: #94a3b8;
}

.page-btn:hover:not(.active):not(:disabled) {
    background: rgba(0, 255, 150, 0.1);
    border-color: #00ff96;
    color: #00ff96;
}

.page-btn.active {
    background: linear-gradient(135deg, #00ff96, #00d4ff);
    color: #0a0e27;
    font-weight: 600;
}

.page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.page-info {
    color: #94a3b8;
    font-size: 0.9rem;
    margin: 0 1rem;
}
/* 操作按钮 */
.action-btns {
    display: flex;
    gap: 0.5rem;
}

.action-btn {
    width: 65px;
    padding: 0.25rem 0.5rem;
    background: rgba(0, 255, 150, 0.1);
    border: 1px solid rgba(0, 255, 150, 0.3);
    border-radius: 4px;
    color: #94a3b8;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
}

.action-btn:hover {
    background: rgba(0, 255, 150, 0.2);
    color: #00ff96;
    border-color: #00ff96;
}

.no-data{
    text-align: center;
}
.submit-btn{
    width: 100%;
    text-align: right;
    margin: 1rem 0;
}

th[style*="position: sticky"],
td[style*="position: sticky"] {
    z-index: 2 !important;
    background-color: rgb(14 25 50) !important;
}

/* 特别为操作列设置更高的层级 */
td[style*="right: 0"] {
    z-index: 3 !important;
}

/* 确保表格容器正确滚动 */
.table-container {
    overflow-x: auto;
    position: relative;
}

/* 调整表格边框和间距，避免滚动时出现空隙 */
table {
    border-collapse: collapse;
    width: 100%;
}
</style>
